import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { useCallback, useState } from 'react';

import { usePaymentMethodCaptureContext } from './PaymentMethodCaptureContext';
import type { PaymentMethodCaptureFormProps } from './PaymentMethodCaptureForm';
import { PaymentMethodCaptureForm } from './PaymentMethodCaptureForm';
import { useCanCapturePaymentMethod } from './useCanCapturePaymentMethod';
import type { TilledPaymentMethodResponse } from 'src/common/external/bambi-api/bambiApi';
import { useTilledPaymentMethodRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';
import { FormField } from 'src/common/FormField';
import { PaymentMethodList } from 'src/common/primitives/PaymentMethod/PaymentMethodList';
import { SectionHeading } from 'src/common/primitives/Section/SectionHeading';

interface ManagePaymentMethodsProps {
  payerId: string;
  payerType: 'passenger' | 'payer_organization';
  billingName?: string;
  selectedPaymentMethodId?: string;
  onChangeBillingDetails?: PaymentMethodCaptureFormProps['onChange'];
  onRemovePaymentMethod?: (id: string) => void;
  onSelectPaymentMethod?: (id: string) => void;
  creditCardFormClassName?: string;
  billingAddressFormClassName?: string;
  paymentMethodListClassName?: string;
}

export function ManagePaymentMethods({
  payerId,
  payerType,
  billingName,
  selectedPaymentMethodId,
  onChangeBillingDetails,
  onRemovePaymentMethod,
  onSelectPaymentMethod,
  creditCardFormClassName,
  billingAddressFormClassName,
  paymentMethodListClassName,
}: ManagePaymentMethodsProps) {
  let paymentMethodListItems: TilledPaymentMethodResponse[] = [];
  const { resetFieldState } = usePaymentMethodCaptureContext();
  const { onlyInvoicingEnabled } = useCanCapturePaymentMethod();

  const {
    isLoading,
    data: paymentMethodListResponse,
    isError,
    refetch,
  } = useTilledPaymentMethodRetrieveQuery(
    {
      payerId,
      payerType,
    },
    { refetchOnMountOrArgChange: true, skip: !payerId }
  );

  if (paymentMethodListResponse && !isError) {
    paymentMethodListItems = paymentMethodListResponse.items;
  } else {
    paymentMethodListItems = [];
  }

  const [showAddNewPaymentMethod, setShowAddNewPaymentMethod] = useState(false);

  const handleUpdatePaymentMethod = useCallback(() => {
    refetch();
  }, [refetch]);

  if (onlyInvoicingEnabled) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <SectionHeading
        heading="Payment Methods"
        subHeading="Manage payment methods"
      />

      {isLoading ? (
        <LoadingIndicator testId="loading-payment-method-list" />
      ) : (
        <PaymentMethodList
          paymentMethods={paymentMethodListItems}
          selectedPaymentMethodId={selectedPaymentMethodId}
          onRemovePaymentMethod={onRemovePaymentMethod}
          onSelectPaymentMethod={onSelectPaymentMethod}
          onUpdatePaymentMethod={handleUpdatePaymentMethod}
          className={paymentMethodListClassName}
        />
      )}
      <FormField
        label="Add new credit card"
        type="switch"
        inputProps={{
          id: 'add-new-credit-card',
          checked: showAddNewPaymentMethod,
          onChange: (value) => {
            setShowAddNewPaymentMethod(value as unknown as boolean);
            if (!value as unknown as boolean) {
              resetFieldState();
            }
          },
        }}
        value={showAddNewPaymentMethod}
      />
      {showAddNewPaymentMethod ? (
        <PaymentMethodCaptureForm
          type="card"
          billingName={billingName}
          onChange={onChangeBillingDetails}
          creditCardFormClassName={creditCardFormClassName}
          billingAddressFormClassName={billingAddressFormClassName}
        />
      ) : null}
    </div>
  );
}
